import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Integration, IntegrationViewKind } from '../../../core/domain/integration';
import type { BackendClient } from '../../bridge/backend-client';
import { overviewCard, statusBadge, statusColor, statusText } from '@vitesse/base-ui/components/templates';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { getSession } from '@vitesse/base-ui';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import '@vitesse/base-ui/elements/vitesse-confirm-dialog';
import { prefetchIntegrationFirstPage } from '../../shell/symbolGrid/pagePrefetch';

const HOVER_DWELL_MS = 30; // match the controller cards' prefetch dwell

/*
 * Catalog GROUP → the glyph that stands for that class of system. Coarse on
 * purpose (see the class comment): six icons cover the whole catalog, and the
 * exact type is spelled out in words right underneath.
 */
const GROUP_ICON: Record<string, string> = {
  Brokers: 'waypoints', // pub/sub fan-out
  'Data stores': 'database',
  Cloud: 'cloud', // a store someone else operates
  'Industrial / OT': 'factory',
  Search: 'search',
  Endpoints: 'globe', // an HTTP API
};

/** The same idea one notch coarser, off a field every instance already carries —
 *  so the icon is right even before the catalog frame lands. */
const VIEW_KIND_ICON: Partial<Record<IntegrationViewKind, string>> = {
  topic: 'waypoints',
  table: 'database',
  endpoint: 'globe',
};

/*
 * The icon for one integration. Group first; if the catalog hasn't landed yet (or
 * added a group this build predates) fall back to the view kind. Only if both are
 * missing do we show the generic integration glyph — i.e. the old behavior, never
 * a blank.
 */
function iconFor(i: Integration): string {
  return GROUP_ICON[i.typeGroup] ?? VIEW_KIND_ICON[i.viewKind] ?? 'database-arrow-up';
}

/*
 * <vitesse-integration-card> — the ONE integration card (the 2nd integration API:
 * DBs/brokers/REST/OT as PLC-like read/write-by-path entities), rendered
 * identically wherever an integration appears (home + the gateway Integrations
 * tab). Self-contained per the platform's Self-Contained Component Pattern: it
 * owns its own confirm-delete flow → Integrations.Delete, so no view re-implements
 * (or forgets) it. Light DOM so the shared card SCSS applies. Mirrors
 * <vitesse-controller-card>.
 *
 * Clicking navigates to the integration detail page (schema tree + properties);
 * the delete affordance is wrapped so its click doesn't bubble into the nav.
 *
 * The live status badge mirrors the controller card; the schema-count chip is the
 * auto-discovered item count (its "symbols"). The type is NOT a badge.
 *
 * ── How the card divides its slots ───────────────────────────────────────────
 * Every slot has exactly ONE job, because a grid of these is read by scanning,
 * not by reading:
 *   ICON    → what CLASS of thing this is (broker vs. store vs. cloud vs. OT).
 *             Per catalog GROUP, not per type: 150 types would need 150 glyphs,
 *             and an icon you have to decode is worse than one you don't.
 *   TEXT    → WHICH one exactly ("MQTT", "NATS" and "Kafka" share an icon), led
 *             by the type so it sits at the same offset on every card.
 *   BADGES  → what it is doing RIGHT NOW. State only, so the colour scan stays
 *             clean — which is why the type is not a badge.
 */
@customElement('vitesse-integration-card')
export class IntegrationCard extends LitElement {
  @property({ attribute: false }) integration?: Integration;
  @property() gatewayId = '';
  /** Friendly gateway label — shown in the flat home views (leading badge),
   *  omitted on the gateway detail page (redundant). */
  @property() gatewayName = '';
  @property({ attribute: false }) backend?: BackendClient;
  @state() private confirmOpen = false;
  @state() private deleting = false;
  @state() private deleteError = '';
  private hoverTimer?: ReturnType<typeof setTimeout>;
  /** Bumped on every session change so the delete affordance re-renders when a
   *  grant is added or revoked mid-session (an admin editing the matrix while
   *  the page is open, or an org switch). */
  @state() private sessionTick = 0;
  private offSession?: () => void;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.offSession = getSession()?.onChange(() => (this.sessionTick += 1));
  }

  // Hover → warm the integration's first schema window so clicking in paints
  // instantly (the controller cards' exact pattern). 30ms dwell skips pass-overs.
  private startPrefetch(): void {
    if (!this.backend || !this.integration) return;
    this.cancelPrefetch();
    const id = this.integration.id;
    this.hoverTimer = setTimeout(() => prefetchIntegrationFirstPage(this.gatewayId, id, this.backend!), HOVER_DWELL_MS);
  }
  private cancelPrefetch(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = undefined;
    }
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cancelPrefetch();
    this.offSession?.();
    this.offSession = undefined;
  }

  /*
   * May the viewer delete — read off the SAME annotation the server enforces
   * (Integrations.Delete requires integration:delete + it_integrations), so the
   * disabled button and the refusal cannot disagree. Fail OPEN with no session:
   * a missing session is a wiring bug, and the server refuses regardless.
   */
  private get canDelete(): boolean {
    return getSession()?.allowsRpc(Integrations.method.delete) !== false;
  }

  /** The sentence explaining the refusal — the one the server would give. */
  private get whyNoDelete(): string {
    return getSession()?.whyRpc(Integrations.method.delete) ?? '';
  }

  private nav(): void {
    if (!this.integration) return;
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'integration', gatewayId: this.gatewayId, integrationId: this.integration.id },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async confirmDelete(): Promise<void> {
    const i = this.integration;
    if (!i || !this.backend || this.deleting || !this.canDelete) return;
    this.deleting = true;
    this.deleteError = '';
    try {
      const res = await this.backend.api.deleteIntegration(this.gatewayId, i.id);
      if (res.ok) this.confirmOpen = false; // the card drops via the integrations stream
      else this.deleteError = res.error || 'Could not delete the integration.';
    } catch (err) {
      this.deleteError = err instanceof Error ? err.message : String(err);
    } finally {
      this.deleting = false;
    }
  }

  render() {
    const i = this.integration;
    if (!i) return nothing;
    /*
     * The wrapping span stops the delete click from bubbling to the card.
     *
     * It ALSO carries the refusal tooltip, and that is not a style choice: a
     * `disabled` button dispatches no mouse events in Chrome, so its own `title`
     * never opens. The hover lands on this span instead, so the explanation has to
     * live here to be readable at all. The button keeps a `title` too, for the
     * assistive tech that reads it off the control rather than the pointer.
     *
     * Refused → disabled + the reason on hover, rather than an enabled button that
     * opens a dialog only to fail on confirm. The detail header already gates the
     * same procedure with <vitesse-gated>; the card is the other surface it
     * appears on, and it was the one still failing late.
     */
    const allowed = this.canDelete;
    const why = allowed ? '' : this.whyNoDelete;
    const actions = html`<span @click=${(e: Event) => e.stopPropagation()} title=${why || nothing}
      >${button({
        label: 'Delete',
        icon: 'trash-2',
        color: 'red',
        size: 'sm',
        disabled: !allowed,
        title: why || undefined,
        onClick: () => (this.confirmOpen = true),
      })}</span
    >`;
    /*
     * "MQTT · mqtt://broker.plant.local:1883" — the type ALWAYS first, then
     * whatever the runtime has to say about this instance.
     *
     * The lead is unconditional, and that is the point. It used to be the runtime
     * summary, falling back to the error, falling back to the constant string
     * "External integration" — so a card told you either nothing or, on the cards
     * that mattered most, only an error. An errored integration is exactly when
     * you most need to know WHAT it is, and it was the one case where the type
     * disappeared. Now the type holds its place and the error follows it.
     *
     * Untranslated: `typeLabel` is a product name ("PostgreSQL", "MQTT"), the
     * same in every locale. It degrades to the raw catalog key if the catalog
     * frame hasn't arrived yet, which still names the type.
     */
    const lead = i.typeLabel || i.type;
    const detail = i.summary || (i.status === 'error' || i.status === 'unauthorized' ? i.errorDetail : '');
    return html`${overviewCard({
        title: i.displayName,
        entityType: 'integration',
        icon: iconFor(i),
        actions,
        description: lead
          ? html`<span class="base-overview-card-description-lead">${lead}</span>${detail ? html` · ${detail}` : nothing}`
          : detail || 'External integration',
        clickable: true,
        onClick: () => this.nav(),
        onHover: () => this.startPrefetch(),
        onHoverEnd: () => this.cancelPrefetch(),
        badges: html`${this.gatewayName ? statusBadge('gateway', this.gatewayName, 'hard-drive') : nothing}${statusBadge(
          statusColor(i.status),
          statusText(i.status),
        )}${i.schemaCount > 0 ? statusBadge('blue', `${i.schemaCount} items`) : nothing}`,
      })}
      ${this.confirmOpen
        ? html`<vitesse-confirm-dialog
            .open=${true}
            heading="Delete integration"
            message=${`Delete "${i.displayName}"?`}
            detail="The connection is torn down and its discovered schema removed. You can add it again."
            confirm-label="Delete"
            danger
            ?busy=${this.deleting}
            error=${this.deleteError}
            @confirm=${() => void this.confirmDelete()}
            @dialog-close=${() => (this.confirmOpen = false)}
          ></vitesse-confirm-dialog>`
        : nothing}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-integration-card': IntegrationCard;
  }
}
